import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import './Navbar.css';
import logoImg from '../../assets/images/logo.png';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const isTransparentBg = location.pathname === '/' || location.pathname.startsWith('/classes/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const offerings = [
    { title: 'Private Yoga', path: '/offerings/private' },
    { title: 'Group Yoga', path: '/offerings/group' },
    { title: 'Meditation', path: '/offerings/meditation' },
    { title: 'Corporate Yoga', path: '/offerings/corporate' },
    { title: 'Online Yoga', path: '/offerings/online' },
    { title: 'Retreat Programs', path: '/offerings/retreat' },
    { title: 'Therapy Yoga', path: '/offerings/therapy' },
  ];

  const classes = [
    { title: 'Hatha Yoga', path: '/classes/hatha' },
    { title: 'Vinyasa Yoga', path: '/classes/vinyasa' },
    { title: 'Ashtanga Yoga', path: '/classes/ashtanga' },
    { title: 'Power Yoga', path: '/classes/power' },
    { title: 'Yin Yoga', path: '/classes/yin' },
    { title: 'Meditation', path: '/classes/meditation' },
    { title: 'Kids Yoga', path: '/classes/kids' },
    { title: 'Women\'s Yoga', path: '/classes/womens' },
    { title: 'Senior Yoga', path: '/classes/senior' },
    { title: 'Personal Training', path: '/classes/personal' },
  ];

  const toggleDropdown = (dropdown) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };

  return (
    <header className={`navbar ${isScrolled || !isTransparentBg ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src={logoImg} alt="Yoga Logo" className="logo-image" />
        </Link>

        {/* Desktop Menu */}
        <nav className="desktop-menu">
          <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>Home</Link>
          <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}>About</Link>
          
          <div className="nav-dropdown" onMouseEnter={() => setActiveDropdown('offerings')} onMouseLeave={() => setActiveDropdown(null)}>
            <button className={`nav-link dropdown-toggle ${(location.pathname.includes('/offerings') || activeDropdown === 'offerings') ? 'active' : ''}`}>
              Offerings <FaChevronDown className="chevron" />
            </button>
            <AnimatePresence>
              {activeDropdown === 'offerings' && (
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  transition={{ duration: 0.2 }}
                  className="dropdown-menu"
                >
                  {offerings.map((item, idx) => (
                    <Link key={idx} to={item.path} className="dropdown-item">{item.title}</Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="nav-dropdown" onMouseEnter={() => setActiveDropdown('classes')} onMouseLeave={() => setActiveDropdown(null)}>
            <button className={`nav-link dropdown-toggle ${(location.pathname.includes('/classes') || activeDropdown === 'classes') ? 'active' : ''}`}>
              Classes <FaChevronDown className="chevron" />
            </button>
            <AnimatePresence>
              {activeDropdown === 'classes' && (
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  transition={{ duration: 0.2 }}
                  className="dropdown-menu"
                >
                  {classes.map((item, idx) => (
                    <Link key={idx} to={item.path} className="dropdown-item">{item.title}</Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/blog" className={`nav-link ${location.pathname === '/blog' ? 'active' : ''}`}>Blog</Link>
          <Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>Contact</Link>
        </nav>

        <div className="navbar-actions">
          <Link to="/appointment" className="btn-appointment">
            Book Appointment
          </Link>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mobile-menu"
          >
            <Link to="/" className="mobile-link">Home</Link>
            <Link to="/about" className="mobile-link">About</Link>
            
            <div className="mobile-dropdown-container">
              <button 
                className="mobile-link mobile-dropdown-toggle"
                onClick={() => toggleDropdown('offerings-mobile')}
              >
                Offerings <FaChevronDown className={`chevron ${activeDropdown === 'offerings-mobile' ? 'open' : ''}`} />
              </button>
              <AnimatePresence>
                {activeDropdown === 'offerings-mobile' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mobile-dropdown-content"
                  >
                    {offerings.map((item, idx) => (
                      <Link key={idx} to={item.path} className="mobile-dropdown-item">{item.title}</Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mobile-dropdown-container">
              <button 
                className="mobile-link mobile-dropdown-toggle"
                onClick={() => toggleDropdown('classes-mobile')}
              >
                Classes <FaChevronDown className={`chevron ${activeDropdown === 'classes-mobile' ? 'open' : ''}`} />
              </button>
              <AnimatePresence>
                {activeDropdown === 'classes-mobile' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mobile-dropdown-content"
                  >
                    {classes.map((item, idx) => (
                      <Link key={idx} to={item.path} className="mobile-dropdown-item">{item.title}</Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/blog" className="mobile-link">Blog</Link>
            <Link to="/contact" className="mobile-link">Contact</Link>
            
            <Link to="/appointment" className="mobile-link mobile-appointment-btn">
              Book Appointment
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
